import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react';

/**
 * ExtendedModeContext — superadmin-only flag global ("EXTENDED MODE").
 *
 * Cuando está ON, se habilita una columna LUPA por row en Publicaciones que
 * abre el ProductDetailModal con la ficha técnica completa del producto
 * (toma raw + catalog state + sources + history + tomas appearance + raw JSON).
 *
 * - Persistido en localStorage (UX debug local; el flag no transporta info
 *   sensible y solo aplica al device).
 * - Toggle vive ÚNICAMENTE en DebugDrawer (no en headers de Publicaciones).
 * - El provider se monta sólo cuando isSuperadmin = true (mismo guard que
 *   DebugDrawer). Para non-superadmins el hook devuelve un stub no-op.
 */

const STORAGE_KEY = 'pulpou_extended_mode';

interface ExtendedModeContextValue {
  extendedMode: boolean;
  setExtendedMode: (v: boolean) => void;
  toggleExtendedMode: () => void;
  available: boolean; // true sólo si el provider está activo (superadmin)
}

const ExtendedModeContext = createContext<ExtendedModeContextValue | undefined>(
  undefined
);

interface ExtendedModeProviderProps {
  enabled: boolean;
  children: ReactNode;
}

export const ExtendedModeProvider: React.FC<ExtendedModeProviderProps> = ({
  enabled,
  children,
}) => {
  const [extendedMode, setExtendedModeState] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    if (!enabled) return false;
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      return raw === '1' || raw === 'true';
    } catch {
      return false;
    }
  });

  // Si el provider se desactiva (ej. logout / cambio de rol), apagar el flag
  // en memoria. NO borramos localStorage para preservar la preferencia si el
  // mismo user vuelve a entrar.
  useEffect(() => {
    if (!enabled && extendedMode) {
      setExtendedModeState(false);
    }
  }, [enabled, extendedMode]);

  const setExtendedMode = useCallback(
    (v: boolean) => {
      if (!enabled) return;
      setExtendedModeState(v);
      try {
        window.localStorage.setItem(STORAGE_KEY, v ? '1' : '0');
      } catch {
        // ignore
      }
    },
    [enabled]
  );

  const toggleExtendedMode = useCallback(() => {
    setExtendedMode(!extendedMode);
  }, [extendedMode, setExtendedMode]);

  const value = useMemo<ExtendedModeContextValue>(
    () => ({
      extendedMode: enabled && extendedMode,
      setExtendedMode,
      toggleExtendedMode,
      available: enabled,
    }),
    [enabled, extendedMode, setExtendedMode, toggleExtendedMode]
  );

  return (
    <ExtendedModeContext.Provider value={value}>
      {children}
    </ExtendedModeContext.Provider>
  );
};

export const useExtendedMode = (): ExtendedModeContextValue => {
  const ctx = useContext(ExtendedModeContext);
  if (!ctx) {
    // Stub para no-superadmins o componentes fuera del provider.
    return {
      extendedMode: false,
      setExtendedMode: () => {},
      toggleExtendedMode: () => {},
      available: false,
    };
  }
  return ctx;
};
